import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { User, UserRole } from "../types/common";
import {
  usersApi,
  LoginRequest,
  SignupRequest,
} from "../api/endpoints/usersApi";
import {
  authApi,
  GoogleAuthRequest,
  FacebookAuthRequest,
} from "../api/endpoints/authApi";
import { decodeJwtPayload } from "../utils/jwt";

interface AuthState {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  loading: boolean;
  error: string | null;
}

const initialState: AuthState = {
  user: null,
  token: localStorage.getItem("authToken"),
  isAuthenticated: !!localStorage.getItem("authToken"),
  loading: false,
  error: null,
};

// Async thunks
export const login = createAsyncThunk(
  "auth/login",
  async (credentials: LoginRequest) => {
    const response = await usersApi.login(credentials);
    localStorage.setItem("authToken", response.token);
    // Extract email from response if backend returns it directly (not in user object)
    const emailFromResponse =
      typeof (response as any).email === "string"
        ? (response as any).email
        : "";

    // The login response doesn't include the user's id directly, but our own
    // backend's JWT carries it as a claim — decode it so ownership/permission
    // checks (e.g. workspace owner/admin) compare against the real id instead
    // of a throwaway placeholder.
    const tokenPayload = decodeJwtPayload(response.token);
    const realUserId = tokenPayload?.userId ?? tokenPayload?.sub;
    const id =
      realUserId !== undefined && realUserId !== null
        ? String(realUserId)
        : "temp-" + Date.now();

    const user: User = {
      id,
      userName: credentials.userName, // Store userName from the login request
      email: emailFromResponse, // Store validated email from the response or fallback
      name: credentials.userName, // Extract name from email
      role: UserRole.MEMBER,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };

    // Try to fetch additional user data from /users/me if available
    try {
      const realUser = await usersApi.getCurrentUser();
      return {
        ...response,
        user: realUser,
      };
    } catch (error) {
      // If /users/me endpoint doesn't exist or fails, use the basic user object we created
      console.warn(
        "Could not fetch user data from /users/me, using data from login",
        error
      );
      return { ...response, user };
    }
  }
);

export const signup = createAsyncThunk(
  "auth/signup",
  async (userData: SignupRequest) => {
    const response = await usersApi.signup(userData);
    localStorage.setItem("authToken", response.token);

    // If backend doesn't return user data, fetch it separately
    if (!response.user) {
      const user = await usersApi.getCurrentUser();
      return { ...response, user };
    }

    return response;
  }
);

export const getCurrentUser = createAsyncThunk(
  "auth/getCurrentUser",
  async () => {
    const user = await usersApi.getCurrentUser();
    return user;
  }
);

export const logout = createAsyncThunk("auth/logout", async () => {
  await usersApi.logout();
  localStorage.removeItem("authToken");
  // Clear redux-persist's cache (auth + workspaces, see store/index.ts) so a
  // different account logging in on the same browser doesn't inherit stale
  // workspace/member data. Cleared directly rather than via `persistor.purge()`
  // to avoid a circular import between this slice and the store module.
  localStorage.removeItem("persist:root");
});

// Google OAuth
export const googleAuth = createAsyncThunk(
  "auth/googleAuth",
  async (data: GoogleAuthRequest) => {
    const response = await authApi.googleAuth(data);
    localStorage.setItem("authToken", response.token);
    return response;
  }
);

// Facebook OAuth
export const facebookAuth = createAsyncThunk(
  "auth/facebookAuth",
  async (data: FacebookAuthRequest) => {
    const response = await authApi.facebookAuth(data);
    localStorage.setItem("authToken", response.token);
    return response;
  }
);

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setUser: (state, action: PayloadAction<User>) => {
      state.user = action.payload;
      state.isAuthenticated = true;
    },
    clearAuth: (state) => {
      state.user = null;
      state.token = null;
      state.isAuthenticated = false;
      localStorage.removeItem("authToken");
    },
  },
  extraReducers: (builder) => {
    // Login
    builder
      .addCase(login.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(login.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload.user || null;
        state.token = action.payload.token;
        state.isAuthenticated = true;
      })
      .addCase(login.rejected, (state, action) => {
        state.loading = false;
        state.error =
          action.error.message ||
          "Login failed. Please check your credentials.";
        state.isAuthenticated = false;
        state.user = null;
        state.token = null;
        localStorage.removeItem("authToken");
      });

    // Signup
    builder
      .addCase(signup.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(signup.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload.user || null;
        state.token = action.payload.token;
        state.isAuthenticated = true;
      })
      .addCase(signup.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Signup failed";
      });

    // Get current user
    builder
      .addCase(getCurrentUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        getCurrentUser.fulfilled,
        (state, action: PayloadAction<User>) => {
          state.loading = false;
          state.user = action.payload;
          state.isAuthenticated = true;
        }
      )
      .addCase(getCurrentUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to get user";
        state.isAuthenticated = false;
        state.user = null;
        state.token = null;
        localStorage.removeItem("authToken");
      });

    // Logout
    builder
      .addCase(logout.pending, (state) => {
        state.loading = true;
      })
      .addCase(logout.fulfilled, (state) => {
        state.loading = false;
        state.user = null;
        state.token = null;
        state.isAuthenticated = false;
      })
      .addCase(logout.rejected, (state) => {
        state.loading = false;
        state.user = null;
        state.token = null;
        state.isAuthenticated = false;
      });

    // Google OAuth
    builder
      .addCase(googleAuth.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(googleAuth.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload.user;
        state.token = action.payload.token;
        state.isAuthenticated = true;
      })
      .addCase(googleAuth.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Google authentication failed";
      });

    // Facebook OAuth
    builder
      .addCase(facebookAuth.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(facebookAuth.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload.user;
        state.token = action.payload.token;
        state.isAuthenticated = true;
      })
      .addCase(facebookAuth.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Facebook authentication failed";
      });
  },
});

export const { clearError, setUser, clearAuth } = authSlice.actions;
export default authSlice.reducer;
